import { FinancialApplicationSection } from "@/types/enums";
import { useAtom, useAtomValue } from "jotai";
import { financialApplicationAtomFamily } from "@/atoms/financialApplicationAtomFamily";
import { useCallback, useMemo } from "react";
import {
  DomainValidationError,
  FinancialApplicationCollectionValidationErrors,
} from "@/types/FinancialApplicationValidationState";
import { sumBy } from "lodash";
import { useFinancialApplicationForm } from "./useFinancialApplicationForm";

export enum FinancialApplicationFormSectionContextType {
  Applicant = "applicant",
  Banker = "banker",
  Task = "task",
}

export interface FinancialApplicationFormSectionValidationResponse {
  responseValidationErrors: Record<string, string[]>;
  isValid: boolean;
}

export enum FinancialApplicationFormValidationDisplayBehavior {
  NeverDisplayValidationErrors = "never",
  AlwaysDisplayValidationErrors = "always",
  DisplayValidationErrorsAfterFirstUserInitiatedValidationOfSection = "display-validation-errors-after-first-user-initiated-validation-of-section",
}

const isErrorIncludedInSectionAndContext = (
  error: DomainValidationError,
  section: FinancialApplicationSection,
  contextType: FinancialApplicationFormSectionContextType
) => {
  if (contextType === FinancialApplicationFormSectionContextType.Applicant) {
    const blocksApplicantSteps = error.customStateDictionary?.["blocksApplicantSteps"];

    if (blocksApplicantSteps == null) {
      return false;
    }

    if (section === FinancialApplicationSection.AllSections) {
      return true;
    }

    const csvOfSteps = blocksApplicantSteps.split(",").map(Number);
    if (csvOfSteps.length > 0 && !csvOfSteps.includes(section)) {
      return false;
    }
  } else if (contextType === FinancialApplicationFormSectionContextType.Banker) {
    const appliesToBankerSections = error.customStateDictionary?.["appliesToBankerSections"];

    if (appliesToBankerSections == null) {
      return false;
    }

    const csvOfSections = appliesToBankerSections.split(",").map(Number);
    if (section === FinancialApplicationSection.AllSections) {
      return true;
    }

    if (csvOfSections.length > 0 && !csvOfSections.includes(section)) {
      return false;
    }
  }

  return true;
};

export const useFinancialApplicationFormSectionValidation = (
  financialApplicationId: string,
  section: FinancialApplicationSection,
  autoValidationBehavior: FinancialApplicationFormValidationDisplayBehavior = FinancialApplicationFormValidationDisplayBehavior.DisplayValidationErrorsAfterFirstUserInitiatedValidationOfSection,
  contextType: FinancialApplicationFormSectionContextType = FinancialApplicationFormSectionContextType.Applicant
) => {
  const financialApplicationFormState = useAtomValue(
    financialApplicationAtomFamily(financialApplicationId)
  );

  const financialApplication = useAtomValue(financialApplicationFormState.financialApplication);

  const { refreshFinancialApplication } = useFinancialApplicationForm(financialApplicationId, contextType);

  const [validationState, setValidationState] = useAtom(
    financialApplicationFormState.validationStateAtomFamily(section)
  );

  const pickCollectionErrors = useCallback(
    (collectionPropertyName: string): FinancialApplicationCollectionValidationErrors => {
      const collectionLevelValidationErrors: DomainValidationError[] = [];
      const collectionMemberValidationErrors: Record<string, DomainValidationError[]> = {};
      const fieldSchemaCollectionLevelValidationErrors: Record<string, string[]> = {};
      const fieldSchemaCollectionMemberValidationErrors: Record<
        string,
        Record<string, string[]>
      > = {};

      for (const error of financialApplication?.validationState?.validationErrors ?? []) {
        if (!isErrorIncludedInSectionAndContext(error, section, contextType)) {
          continue;
        }

        const firstPartOfPropertyName = error.propertyName?.split(".")[0];
        const restOfPropertyName = error.propertyName?.split(".").slice(1).join(".");

        const collectionMatcher = firstPartOfPropertyName?.match(
          `^${collectionPropertyName}\\[([a-zA-Z0-9\\-]*)\\].*`
        );

        const collectionMemberId = collectionMatcher?.[1];

        const isCollectionLevel = firstPartOfPropertyName === collectionPropertyName;

        if (collectionMemberId && restOfPropertyName) {
          collectionMemberValidationErrors[collectionMemberId] =
            collectionMemberValidationErrors[collectionMemberId] ?? [];
          collectionMemberValidationErrors[collectionMemberId].push(error);
          fieldSchemaCollectionMemberValidationErrors[collectionMemberId] =
            fieldSchemaCollectionMemberValidationErrors[collectionMemberId] ?? {};
          fieldSchemaCollectionMemberValidationErrors[collectionMemberId][restOfPropertyName] =
            fieldSchemaCollectionMemberValidationErrors[collectionMemberId][restOfPropertyName] ??
            [];
          fieldSchemaCollectionMemberValidationErrors[collectionMemberId][restOfPropertyName].push(
            error.errorMessage ?? ""
          );
        } else if (isCollectionLevel) {
          collectionLevelValidationErrors.push(error);
          fieldSchemaCollectionLevelValidationErrors[restOfPropertyName ?? ""] =
            fieldSchemaCollectionLevelValidationErrors[restOfPropertyName ?? ""] ?? [];
          fieldSchemaCollectionLevelValidationErrors[restOfPropertyName ?? ""].push(
            error.errorMessage ?? ""
          );
        }
      }

      return {
        pathToCollection: collectionPropertyName,
        collectionLevelValidationErrors,
        collectionMemberValidationErrors,
        fieldSchemaCollectionLevelValidationErrors,
        fieldSchemaCollectionMemberValidationErrors,
      };
    },
    [financialApplication?.validationState, contextType]
  );

  const validationErrorsForSection = useMemo(() => {
    const errors: Record<string, DomainValidationError[]> = {};

    for (const error of financialApplication?.validationState?.validationErrors ?? []) {
      if (!isErrorIncludedInSectionAndContext(error, section, contextType)) {
        continue;
      }

      errors[error.propertyName ?? ""] = errors[error.propertyName ?? ""] ?? [];
      errors[error.propertyName ?? ""].push(error);
    }

    return errors;
  }, [financialApplication?.validationState, section, contextType]);

  const totalRequiredFieldCount = useMemo(() => {
    let totalRequiredFieldCount = financialApplication?.validationState?.requiredFieldCount;
    const nonRequiredErrors = financialApplication?.validationState?.validationErrors.filter(x => !x.isRequiredErrorCode);
    totalRequiredFieldCount = (totalRequiredFieldCount ?? 0) + (nonRequiredErrors?.length ?? 0);
    return totalRequiredFieldCount ?? 0;
  }, [financialApplication?.validationState]);

  const collectionValidationErrorsForSection = useMemo(() => {
    const collections = [
      "DepositApplications",
      "KeyPeople",
      "JointAccountOwners",
      "PersonalAccountBeneficiaries",
    ];

    return collections.reduce(
      (acc, collection) => {
        acc[collection] = pickCollectionErrors(collection);
        return acc;
      },
      {} as Record<string, FinancialApplicationCollectionValidationErrors>
    );
  }, [financialApplication?.validationState, pickCollectionErrors]);

  const fieldSchemaValidationErrorsForSection = useMemo(() => {
    return Object.entries(validationErrorsForSection).reduce(
      (acc, [key, value]) => {
        acc[key] = value.map((error) => error.errorMessage ?? "");
        return acc;
      },
      {} as Record<string, string[]>
    );
  }, [validationErrorsForSection]);

  const getValidationErrorsForSectionWithPredicate = useCallback(
    (predicate: (error: DomainValidationError) => boolean) => {
      return Object.entries(validationErrorsForSection).reduce(
        (acc, [key, value]) => {
          acc[key] = value.filter(predicate).map((error) => error.errorMessage ?? "");
          return acc;
        },
        {} as Record<string, string[]>
      );
    },
    [validationErrorsForSection]
  );

  const validationErrorsCountIncludingCollectionErrors = useMemo(() => {
    const errorsCount = sumBy(Object.values(validationErrorsForSection), "length");
    return errorsCount;
  }, [validationErrorsForSection]);

  const hasValidationErrors = useMemo(() => {
    return validationErrorsCountIncludingCollectionErrors > 0;
  }, [validationErrorsCountIncludingCollectionErrors]);

  const validateFinancialApplicationFormSection: () => Promise<FinancialApplicationFormSectionValidationResponse> =
    useCallback(async () => {
      // Return the current validation state without making API calls
      setValidationState((prev) => ({
        ...prev,
        userEverInitiatedValidationForSection: true,
      }));

      try {
        const refreshedApplication = await refreshFinancialApplication();

        if (refreshedApplication.validationState == null) {
          return {
            responseValidationErrors: {},
            isValid: false,
          };
        }

        const hasErrors = refreshedApplication.validationState.validationErrors.some((error) =>
          isErrorIncludedInSectionAndContext(error, section, contextType)
        );

        return {
          responseValidationErrors: fieldSchemaValidationErrorsForSection, // todo note that these may be stale, but they are not being used anywhere anyway.
          isValid: !hasErrors,
        };
      } catch (error) {
        return {
          responseValidationErrors: {},
          isValid: false,
        };
      }
    }, [
      fieldSchemaValidationErrorsForSection,
      setValidationState,
      refreshFinancialApplication,
      section,
      contextType,
    ]);

  const validationErrors = useMemo(() => {
    switch (autoValidationBehavior) {
      case FinancialApplicationFormValidationDisplayBehavior.NeverDisplayValidationErrors:
        return {};
      case FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors:
        return fieldSchemaValidationErrorsForSection;
      case FinancialApplicationFormValidationDisplayBehavior.DisplayValidationErrorsAfterFirstUserInitiatedValidationOfSection:
        return validationState.userEverInitiatedValidationForSection
          ? fieldSchemaValidationErrorsForSection
          : getValidationErrorsForSectionWithPredicate((error) => !error.isRequiredErrorCode);
    }
  }, [
    autoValidationBehavior,
    validationState.userEverInitiatedValidationForSection,
    fieldSchemaValidationErrorsForSection,
    hasValidationErrors,
  ]);

  const getFilteredCollectionValidationErrors = useCallback(
    (
      collectionValidationErrors: FinancialApplicationCollectionValidationErrors,
      predicate: (error: DomainValidationError) => boolean
    ) => {
      const filtered: FinancialApplicationCollectionValidationErrors = {
        pathToCollection: collectionValidationErrors.pathToCollection,
        collectionLevelValidationErrors:
          collectionValidationErrors.collectionLevelValidationErrors.filter(predicate),
        collectionMemberValidationErrors: Object.fromEntries(
          Object.entries(collectionValidationErrors.collectionMemberValidationErrors).filter(
            ([_, errors]) => errors.some(predicate)
          )
        ),
        fieldSchemaCollectionLevelValidationErrors:
          collectionValidationErrors.fieldSchemaCollectionLevelValidationErrors,
        fieldSchemaCollectionMemberValidationErrors: {},
      };

      for (const [memberId, errors] of Object.entries(
        collectionValidationErrors.collectionMemberValidationErrors
      )) {
        filtered.fieldSchemaCollectionMemberValidationErrors[memberId] = {};
        for (const error of errors) {
          if (predicate(error)) {
            const propertyName = error.propertyName?.split(".").slice(1).join(".") ?? "";
            filtered.fieldSchemaCollectionMemberValidationErrors[memberId][propertyName] =
              filtered.fieldSchemaCollectionMemberValidationErrors[memberId][propertyName] ?? [];
            filtered.fieldSchemaCollectionMemberValidationErrors[memberId][propertyName].push(
              error.errorMessage ?? ""
            );
          }
        }
      }

      return filtered;
    },
    [collectionValidationErrorsForSection]
  );

  const getCollectionValidationErrors = useCallback(
    (collectionPropertyName: string) => {
      const collectionValidationErrors = collectionValidationErrorsForSection[
        collectionPropertyName
      ] ?? {
        pathToCollection: collectionPropertyName,
        collectionLevelValidationErrors: [],
        collectionMemberValidationErrors: {},
        fieldSchemaCollectionLevelValidationErrors: {},
        fieldSchemaCollectionMemberValidationErrors: {},
      };

      switch (autoValidationBehavior) {
        case FinancialApplicationFormValidationDisplayBehavior.NeverDisplayValidationErrors:
          return {
            ...collectionValidationErrors,
            collectionLevelValidationErrors: [],
            collectionMemberValidationErrors: {},
            fieldSchemaCollectionLevelValidationErrors: {},
            fieldSchemaCollectionMemberValidationErrors: {},
          };
        case FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors:
          return collectionValidationErrors;
        case FinancialApplicationFormValidationDisplayBehavior.DisplayValidationErrorsAfterFirstUserInitiatedValidationOfSection:
          return validationState.userEverInitiatedValidationForSection
            ? collectionValidationErrors
            : getFilteredCollectionValidationErrors(
                collectionValidationErrors,
                (error) => !error.isRequiredErrorCode
              );
      }
    },
    [
      collectionValidationErrorsForSection,
      autoValidationBehavior,
      validationState.userEverInitiatedValidationForSection,
    ]
  );

  const allValidationErrorsForContext = useMemo(() => {
    return financialApplication?.validationState?.validationErrors ?? [];
  }, [financialApplication?.validationState?.validationErrors]);

  return {
    validateFinancialApplicationFormSection,
    hasValidationErrors,
    validationErrors,
    validationErrorsForSection,
    getCollectionValidationErrors,
    validationErrorsCountIncludingCollectionErrors,
    allValidationErrorsForContext,
    totalRequiredFieldCount,
  };
};
